/**
 * lazyWithRetry — drop-in replacement for React.lazy that survives webpack
 * chunk-load failures triggered by stale `bundle.js` references after a
 * redeploy / hot-reload cycle.
 *
 * Why this is needed:
 *   When webpack emits a new `bundle.js` with new chunk hashes, any tab
 *   that already has the OLD bundle.js in memory keeps trying to fetch
 *   chunks under their OLD names. Those URLs return the SPA index.html
 *   fallback, which the browser fails to parse → `ChunkLoadError` and a
 *   crashed dashboard view.
 *
 * Recovery strategy (executed at most once per tab, gated by sessionStorage):
 *   1. Unregister all service workers and wipe all `caches.*` entries —
 *      otherwise the stale SW would just re-serve the broken bundle.
 *   2. Call `window.location.reload()` so the fresh bundle.js and the
 *      matching chunk manifest are pulled from the server.
 *
 * After one auto-reload we DON'T retry again (that would loop forever if
 * a chunk is genuinely missing on the server). Instead the original
 * ChunkLoadError is allowed to bubble so React/error-boundaries can show
 * a normal failure UI.
 */
const RECOVERED_KEY = '__rupiwise_chunk_recovered';

function isChunkLoadFailure(err) {
  if (!err) return false;
  const name = String(err.name || '');
  const msg = String(err.message || err);
  return (
    name === 'ChunkLoadError' ||
    msg.includes('Loading chunk') ||
    msg.includes('Loading CSS chunk') ||
    (msg.includes("expected expression") && msg.includes("'<'"))
  );
}

async function clearClientCaches() {
  const jobs = [];
  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    jobs.push(
      navigator.serviceWorker.getRegistrations()
        .then((regs) => Promise.all(regs.map((r) => r.unregister())))
        .catch(() => {})
    );
  }
  if (typeof caches !== 'undefined') {
    jobs.push(
      caches.keys()
        .then((keys) => Promise.all(keys.map((k) => caches.delete(k))))
        .catch(() => {})
    );
  }
  await Promise.all(jobs);
}

export function lazyWithRetry(factory) {
  // We DO NOT pre-import React.lazy at the top of this file — callers
  // pass it in implicitly via React's own runtime. Returning a wrapped
  // dynamic-import factory is enough.
  return () =>
    factory().catch((err) => {
      if (!isChunkLoadFailure(err)) throw err;
      // Already attempted recovery in this tab? Surface the error so the
      // user sees the React error boundary instead of looping.
      try {
        if (sessionStorage.getItem(RECOVERED_KEY) === '1') throw err;
        sessionStorage.setItem(RECOVERED_KEY, '1');
      } catch (_) {
        // sessionStorage blocked (private mode / iframe) — fall through.
      }
      // Fire the recovery in the background. We `throw err` afterwards so
      // React's Suspense doesn't render a stale resolved promise; the
      // upcoming `window.location.reload()` makes that throw moot.
      clearClientCaches().finally(() => {
        try { window.location.reload(); } catch (_) {}
      });
      // Return a never-resolving promise so React holds Suspense until
      // the reload kicks in. This avoids flashing an error overlay.
      return new Promise(() => {});
    });
}

export default lazyWithRetry;
